import { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import API from '@/lib/api';

export const ScanArticleInput = ({ id, value, onChange, onSelect, autoFocus, onSearchBusy }) => {
  const input = useRef(null);
  const busyCallback = useRef(onSearchBusy);
  busyCallback.current = onSearchBusy;
  const reportBusy = useCallback(busy => busyCallback.current?.(id, busy), [id]);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [active, setActive] = useState(0);
  const [retry, setRetry] = useState(0);
  const query = (value || '').trim();
  const options = result?.query === query ? result.items || [] : [];
  useEffect(() => { if (autoFocus) input.current?.focus(); }, [autoFocus]);
  useEffect(() => {
    if (!open || query.length < 2) { setResult(null); reportBusy(false); return; }
    reportBusy(true);
    let current = true;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const r = await API.get('/artikels-zoek', { params: { q: query, limit: 8 }, noCache: true, signal: controller.signal, timeout: 15000 });
        if (current) { setResult({ query, items: r.data || [] }); setActive(0); }
      } catch (e) {
        if (current && e.code !== 'ERR_CANCELED') setResult({ query, error: true });
      } finally { if (current) reportBusy(false); }
    }, 200);
    return () => { current = false; clearTimeout(timer); controller.abort(); reportBusy(false); };
  }, [open, query, retry, reportBusy]);
  const choose = article => { setOpen(false); onSelect(article); reportBusy(false); };
  return <Popover open={open && query.length >= 2} onOpenChange={setOpen}>
    <PopoverAnchor asChild>
      <input ref={input} id={`scan-article-${id}`} value={value || ''} placeholder="Zoek artikel…" autoComplete="off"
        role="combobox" aria-expanded={open && query.length >= 2} aria-controls={`scan-article-options-${id}`} aria-autocomplete="list"
        aria-activedescendant={open && options[active] ? `scan-article-option-${id}-${active}` : undefined}
        onFocus={() => setOpen(true)} onClick={() => setOpen(true)} onChange={e => { reportBusy(e.target.value.trim().length >= 2); onChange(e.target.value); setOpen(true); }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setOpen(false); return; }
          if (e.key === 'Tab') { setOpen(false); return; }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault(); setOpen(true);
            setActive(v => Math.max(0, Math.min(options.length - 1, v + (e.key === 'ArrowDown' ? 1 : -1))));
          }
          if (e.key === 'Enter') { e.preventDefault(); if (open && options[active]) choose(options[active]); else setOpen(true); }
        }} className="field-input w-full text-xs font-mono !py-2" data-testid={`scan-art_code-${id}`} />
    </PopoverAnchor>
    <PopoverContent align="start" collisionPadding={8} onOpenAutoFocus={e => e.preventDefault()} onCloseAutoFocus={e => e.preventDefault()}
      onInteractOutside={e => { if (e.target === input.current) e.preventDefault(); }}
      style={{ maxHeight: 'min(16rem, var(--radix-popover-content-available-height))' }}
      className="!p-1 w-80 max-w-[calc(100vw-16px)] overflow-y-auto" data-testid={`scan-article-dropdown-${id}`}>
      <div role="listbox" id={`scan-article-options-${id}`} aria-label="Artikelen">
        {options.map((article, index) => <button key={`${article.id}-${index}`} id={`scan-article-option-${id}-${index}`} role="option"
          aria-selected={active === index} type="button" onMouseEnter={() => setActive(index)} onMouseDown={e => e.preventDefault()} onClick={() => choose(article)}
          className={`block text-left w-full p-2 text-sm rounded ${active === index ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
          data-testid={`scan-article-result-${id}-${index}`}>
          <span className="font-semibold break-words">{article.omschrijving}</span>
          <span className="block text-xs text-gray-500 break-words">{article._variant_match?.art_code || article._variant_match?.code || article.art_code || article.ean}
            {article._variant_match?.combo && ` · ${Object.values(article._variant_match.combo).join(' · ')}`}</span>
        </button>)}
        {result?.query === query && result.error ? <div role="alert" className="p-2 text-xs text-red-700" data-testid={`scan-article-search-error-${id}`}>
          Artikelen konden niet worden geladen. <button type="button" className="underline" onClick={() => { setResult(null); setRetry(v => v + 1); }} data-testid={`scan-article-retry-${id}`}>Opnieuw</button>
        </div> : !options.length && <div role="status" className="p-2 text-xs text-gray-500" data-testid={`scan-article-search-status-${id}`}>
          {result?.query === query ? 'Geen artikelen gevonden' : 'Zoeken…'}</div>}
      </div>
    </PopoverContent>
  </Popover>;
};